import type { ApDocumentType } from "@/accounts-payable/types";
import { QUICKBOOKS_BILL_PUSH_STATUS } from "@/business/quickbooks-query";

export const invoicesKey = (businessId?: string | null) =>
  ["ap-invoices", businessId] as const;
export const receiptsKey = (businessId?: string | null) =>
  ["ap-receipts", businessId] as const;

export const AP_DOCUMENT_ENDPOINTS = {
  invoice: { path: "invoices", listKey: invoicesKey, runsParam: "invoice_id" },
  receipt: { path: "receipts", listKey: receiptsKey, runsParam: "receipt_id" },
} as const satisfies Record<
  ApDocumentType,
  {
    path: string;
    listKey: (businessId?: string | null) => readonly unknown[];
    runsParam: string;
  }
>;
export const agentRunsKey = (businessId?: string | null) =>
  ["ap-agent-runs", businessId] as const;
export const emailsKey = (businessId?: string | null) =>
  ["ap-emails", businessId] as const;
export const vendorsKey = (businessId?: string | null) =>
  ["ap-vendors", businessId] as const;
export const reviewQueueKey = (businessId?: string | null) =>
  ["ap-review-queue", businessId] as const;
/** Not under reviewQueueKey: that prefix holds only paginated queues, which
 * the queue mutations update in place. */
export const reviewQueueVendorsKey = (businessId?: string | null) =>
  ["ap-review-queue-vendors", businessId] as const;
export const assigneesKey = (businessId?: string | null) =>
  ["ap-assignees", businessId] as const;
export const automatedInvoicesKey = (businessId?: string | null) =>
  ["ap-automated-invoices", businessId] as const;
export const invoiceDocumentKey = (documentSrc?: string | null) =>
  ["ap-invoice-document", documentSrc] as const;
export const jobSearchKey = (
  businessId?: string | null,
  query?: string | null,
  limit?: number,
) => ["ap-job-search", businessId, query ?? "", limit ?? null] as const;

export const projectSearchKey = (
  businessId?: string | null,
  query?: string | null,
) => ["ap-project-search", businessId, query ?? ""] as const;

export const pricebookSearchKey = (
  businessId?: string | null,
  query?: string | null,
) => ["ap-pricebook-search", businessId, query ?? ""] as const;

export const documentPurchaseOrdersKey = (
  businessId?: string | null,
  documentId?: string | null,
) => ["ap-po-search", businessId, documentId] as const;
export const purchaseOrderSearchKey = (
  businessId?: string | null,
  documentId?: string | null,
  query?: string | null,
) =>
  [...documentPurchaseOrdersKey(businessId, documentId), query ?? ""] as const;

export const invoiceKey = (
  businessId?: string | null,
  invoiceId?: string | null,
) => [...invoicesKey(businessId), "detail", invoiceId] as const;
export const emailKey = (businessId?: string | null, emailId?: string | null) =>
  [...emailsKey(businessId), "detail", emailId] as const;
export const vendorKey = (
  businessId?: string | null,
  vendorId?: string | null,
) => [...vendorsKey(businessId), "detail", vendorId] as const;
export const invoiceQuickbooksBillKey = (
  businessId?: string | null,
  invoiceId?: string | null,
) =>
  [...invoiceKey(businessId, invoiceId), QUICKBOOKS_BILL_PUSH_STATUS] as const;
export const invoiceAgentRunsKey = (
  businessId?: string | null,
  invoiceId?: string | null,
) => [...agentRunsKey(businessId), invoiceId] as const;
export const receiptKey = (
  businessId?: string | null,
  receiptId?: string | null,
) => [...receiptsKey(businessId), "detail", receiptId] as const;
export const documentAgentRunsKey = invoiceAgentRunsKey;
